/**
 * Workspace shell — unified Parcel entry.
 *
 * Boot sequence (mirrors RUNNER_ARCHITECTURE.md §Boot sequence):
 *  1. Early lang/theme from localStorage (synchronous, first-paint)
 *  2. bootShell: polyglot init → ensureSDKInit → registerSdkAdapter
 *     → auth gate → eager USER/ORG/WORKSPACE/WP fetch → mount shell
 *  3. dispatchUrl: initial route → lazy-load sub-app or shared page into Slot
 *  4. wireGlobalListeners: popstate + cross-tab storage events
 *
 * Phase 8: app manifests imported from each sub-app package.
 * Each manifest declares key, routes, i18nNamespaces, app/context lazy
 * imports, and optional fetchOnMount / onParamsChange lifecycle hooks.
 */

import { bootShell } from '../shared/functions/bootShell.js'
import { wireGlobalListeners } from './wireGlobalListeners.js'
import context from './context.js'

import canvasManifest      from '../canvas/manifest.js'
import wpManifest          from '../workspace-project/manifest.js'
import marketplaceManifest from '../marketplace/manifest.js'
import previewManifest     from '../preview/manifest.js'

// Phase 8: full manifests replace Phase 7 stubs.
// Keyed by the app identifier used in APP_DISPATCH ('app' field).
// (cms/inspect/gazer packages aren't on disk in this branch — re-add the
// imports when those sub-apps land.)
const apps = {
  workspaceProject: wpManifest,
  canvas:           canvasManifest,
  marketplace:      marketplaceManifest,
  preview:          previewManifest,
}

// Use the smbls default router — pages are rendered into the 'content'
// element at the shell root by the framework's built-in routing mechanism.
// All routes live in context.pages (merged from every sub-app) and the
// smbls router matches URL → page automatically on initial render and on
// popstate. No customRouterElement or dispatchUrl needed.

;(async () => {
  const shell = await bootShell({
    apps,
    defaultApp: 'workspaceProject',
    fallbackRoute: '/',
    context
  })

  if (!shell) {
    // Auth gate bounced — bootShell already set URL to /login and returned null.
    // Mount the shell manually so auth pages (login, signup, etc.) render.
    // The smbls router fires onRenderRouter during create() and auto-routes
    // to /login (which is in context.pages via wpPages), so no manual
    // dispatchUrl call is needed.
    const { create } = await import('smbls')
    const { getSDK } = await import('../shared/functions/sdk.js')

    try {
      const sdk = getSDK()
      if (!context.methods) context.methods = {}
      context.methods.sdk = sdk
    } catch (e) {
      console.warn('[workspace-shell] auth-bounce SDK init failed:', e?.message || e)
    }

    if (context.state && !Array.isArray(context.state.notifications)) {
      context.state.notifications = []
    }

    const authShell = await create(context.app, context)
    wireGlobalListeners(authShell, { apps, fallbackRoute: '/login' })
    return
  }

  wireGlobalListeners(shell, { apps, fallbackRoute: '/' })
})()
